import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { VioTablesV2Component } from 'vio-tables-v2';
import type { VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { ApiRequestsService } from '../../services/api-requests.service';

declare const bootstrap: any;

interface Item {
  _id?: string;
  itemCode: string;
  title: string;
  author: string;
  price: number | null;
  genre: string;
  description: string;
  imageS3Key?: string;
  imageUrl?: string;
  mimeType?: string;
}

@Component({
  selector: 'app-item-master',
  standalone: true,
  imports: [CommonModule, FormsModule, VioTablesV2Component],
  templateUrl: './item-master.component.html',
  styleUrl: './item-master.component.scss',
})
export class ItemMasterComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('addItemModal') addItemModalRef!: ElementRef<HTMLElement>;
  @ViewChild('editItemModal') editItemModalRef!: ElementRef<HTMLElement>;

  items: Item[] = [];
  itemsForTable: any[] = [];
  isLoading = false;
  isSaving = false;
  errorMessage = '';
  successMessage = '';

  newItem: Partial<Item> = this.emptyItem();
  editItem: Partial<Item & { _id: string }> = {};
  newImageFile: File | null = null;
  editImageFile: File | null = null;
  newImagePreview: string | null = null;
  editImagePreview: string | null = null;

  private addModal: any;
  private editModal: any;

  tableConfig: VT2_TABLE_CONFIG = {
    tableTitle: 'Item Master (Books)',
    excelFileName: 'item-master',
    columnLabels: {
      itemCode: 'Code',
      title: 'Title',
      author: 'Author',
      genre: 'Genre',
      price: 'Price (₹)',
      description: 'Description',
    },
    excludeFields: ['_id', 'imageS3Key', 'imageUrl', 'mimeType', 'isActive', 'createdAt', 'updatedAt'],
    isSortable: true,
    showFormulaButton: false,
    showSummationButton: false,
    showHeaderGroupButton: false,
    actionButtons: [],
  };

  constructor(private apiService: ApiRequestsService) {}

  ngOnInit(): void {
    this.loadItems();
  }

  ngAfterViewInit(): void {
    if (this.addItemModalRef?.nativeElement) {
      document.body.appendChild(this.addItemModalRef.nativeElement);
      this.addModal = new bootstrap.Modal(this.addItemModalRef.nativeElement);
    }
    if (this.editItemModalRef?.nativeElement) {
      document.body.appendChild(this.editItemModalRef.nativeElement);
      this.editModal = new bootstrap.Modal(this.editItemModalRef.nativeElement);
    }

    this.tableConfig = {
      ...this.tableConfig,
      actionButtons: [
        {
          icon: '✏️',
          label: 'Edit',
          title: 'Edit item',
          onClick: (row: any) => this.openEdit(row),
        },
        {
          icon: '🗑️',
          label: 'Remove',
          title: 'Deactivate item',
          onClick: (row: any) => this.deactivate(row._id),
        },
      ],
    };
  }

  ngOnDestroy(): void {
    this.addModal?.hide();
    this.editModal?.hide();
    this.addItemModalRef?.nativeElement?.remove();
    this.editItemModalRef?.nativeElement?.remove();
  }

  private emptyItem(): Partial<Item> {
    return { itemCode: '', title: '', author: '', price: null, genre: '', description: '' };
  }

  loadItems(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.apiService.getApiCall('api/v1/items').subscribe({
      next: (res: any) => {
        this.items = res.Data || [];
        this.itemsForTable = this.items.map(it => ({ ...it }));
        this.isLoading = false;
      },
      error: (err: any) => {
        this.errorMessage = err?.error?.errorDescription || 'Failed to load items.';
        this.isLoading = false;
      },
    });
  }

  openAdd(): void {
    this.newItem = this.emptyItem();
    this.newImageFile = null;
    this.newImagePreview = null;
    this.errorMessage = '';
    this.successMessage = '';
    this.addModal?.show();
  }

  onNewImageChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.newImageFile = file;
    const reader = new FileReader();
    reader.onload = (e) => { this.newImagePreview = e.target?.result as string; };
    reader.readAsDataURL(file);
  }

  saveNewItem(): void {
    if (!this.newItem.title?.trim()) { this.errorMessage = 'Title is required.'; return; }
    if (!this.newItem.itemCode?.trim()) { this.errorMessage = 'Item code is required.'; return; }
    if (!this.newImageFile) { this.errorMessage = 'Image is required.'; return; }

    this.isSaving = true;
    this.errorMessage = '';

    const form = new FormData();
    form.append('itemCode', this.newItem.itemCode!.trim());
    form.append('title', this.newItem.title!.trim());
    form.append('author', this.newItem.author?.trim() || '');
    form.append('price', this.newItem.price != null ? String(this.newItem.price) : '');
    form.append('genre', this.newItem.genre?.trim() || '');
    form.append('description', this.newItem.description?.trim() || '');
    form.append('image', this.newImageFile);

    this.apiService.uploadMultiple(form, 'api/v1/items/create').subscribe({
      next: () => {
        this.isSaving = false;
        this.addModal?.hide();
        this.successMessage = 'Item created successfully.';
        this.loadItems();
      },
      error: (err: any) => {
        this.errorMessage = err?.error?.errorDescription || 'Failed to create item.';
        this.isSaving = false;
      },
    });
  }

  openEdit(item: any): void {
    this.editItem = { ...item };
    this.editImageFile = null;
    this.editImagePreview = item.imageUrl || null;
    this.errorMessage = '';
    this.successMessage = '';
    this.editModal?.show();
  }

  onEditImageChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.editImageFile = file;
    const reader = new FileReader();
    reader.onload = (e) => { this.editImagePreview = e.target?.result as string; };
    reader.readAsDataURL(file);
  }

  saveEdit(): void {
    if (!this.editItem.title?.trim()) { this.errorMessage = 'Title is required.'; return; }

    this.isSaving = true;
    this.errorMessage = '';

    const form = new FormData();
    form.append('title', this.editItem.title!.trim());
    form.append('author', this.editItem.author?.trim() || '');
    form.append('price', this.editItem.price != null ? String(this.editItem.price) : '');
    form.append('genre', this.editItem.genre?.trim() || '');
    form.append('description', this.editItem.description?.trim() || '');
    if (this.editImageFile) form.append('image', this.editImageFile);

    this.apiService.putFormData(`api/v1/items/${this.editItem._id}`, form).subscribe({
      next: () => {
        this.isSaving = false;
        this.editModal?.hide();
        this.successMessage = 'Item updated successfully.';
        this.loadItems();
      },
      error: (err: any) => {
        this.errorMessage = err?.error?.errorDescription || 'Failed to update item.';
        this.isSaving = false;
      },
    });
  }

  deactivate(id: string): void {
    if (!confirm('Remove this item? It will no longer appear in template creation.')) return;
    this.apiService.deleteApiCall(`api/v1/items/${id}`).subscribe({
      next: () => {
        this.successMessage = 'Item removed.';
        this.loadItems();
      },
      error: (err: any) => {
        this.errorMessage = err?.error?.errorDescription || 'Failed to remove item.';
      },
    });
  }
}
